/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';

import { useQuery } from '@apollo/react-hooks';
import styled from 'styled-components';
import { Add } from 'WeployDesignLanguage/icons';
import Color from 'color';
import { get } from 'lodash';

import repostIllustration from '../../../components/weployer/jobTypeSelectionModal/repost_illustration.svg';
import { navigateExternal } from '../common/navigation';
import JobsRepostingEmptyState from '../../../components/weployer/jobs/templates/JobsRepostingEmptyState';
import RepostableJobsTable from '../../../components/weployer/jobs/templates/RepostableJobsTable';
import { GET_REPOSTABLE_JOBS } from '../jobs/graphql/queries';
import InfiniteScroll from '../InfiniteScroll';
import DirectDescription from '../../../components/weployer/jobTypeSelectionModal/DirectDescription';
import SelectDescription from '../../../components/weployer/jobTypeSelectionModal/SelectDescription';
import RehireJobDescription from '../../../components/weployer/jobTypeSelectionModal/RehireJobDescription';

import {
  CREATE_JOB_PATH,
  CREATE_REHIRE_JOB_PATH,
  REPOST_JOB_PATH,
  REPOST_REHIRE_JOB_PATH,
  getExternalPath,
} from '../pathHelpers';

import { JOB_TYPES } from './JobTypeSelectionModal';

const StyledPageWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-bottom: ${props => props.theme.spacing[8]};
  overflow: auto;
`;

const StyledPageHeader = styled.div`
  display: flex;
  margin-bottom: ${props => props.theme.spacing[7]};
  justify-content: space-between;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledPageDescription = styled.div`
  max-width: 60%;
  margin: ${props => props.theme.spacing[3]} 0 0;

  @media (max-width: 700px) {
    order: 1;
    max-width: 100%;
    width: 100%;
  }
`;

const StyledPostBlankJobButton = styled.button`
  margin: ${props => props.theme.spacing[3]} 0 0;
  border: 2px dashed rgba(0, 0, 0, 0.12);
  border-radius: ${props => props.theme.borderRadius.medium};
  background-color: white;
  width: 200px;
  height: 200px;
  cursor: pointer;
  color: ${props => props.theme.colors.text.primary};
  transition: all 0.150s ease-in-out;

  &:hover {
    background-color: ${props => Color(props.theme.colors.text.primary).fade(0.90).string()};
  }

  &:focus {
    outline: none;
    border: 4px solid ${props => props.theme.colors.brand.primary};
  }

  @media (max-width: 700px) {
    order: 2;
  }
`;

const StyledPostBlankJobButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  & > svg {
    font-size: 100px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const StyledSectionTitle = styled.h5`
  ${props => props.theme.typography.h5}
`;

const StyledSectionSubtitle = styled.h6`
  ${props => props.theme.typography.subtitle1};
  color: ${props => props.theme.colors.text.seconday};
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const StyledRepostIllustration = styled.img`
  width: 95px;
  margin-right: 16px;
`;

const DEFAULT_PAGE_SIZE = 20;

const repostJobPath = (jobType, repostParentId) => {
  let path = '';

  switch (jobType) {
    case JOB_TYPES.DIRECT:
      path = getExternalPath({ pathName: REPOST_JOB_PATH, params: { repost_parent_id: repostParentId, instant: true } });
      break;
    case JOB_TYPES.SELECT:
      path = getExternalPath({ pathName: REPOST_JOB_PATH, params: { repost_parent_id: repostParentId, instant: false } });
      break;
    case JOB_TYPES.REHIRE:
      path = getExternalPath({ pathName: REPOST_REHIRE_JOB_PATH, params: { repost_parent_id: repostParentId } });
      break;

    default:
      break;
  }

  return path;
};

const blankJobPath = (jobType) => {
  let path = '';

  switch (jobType) {
    case JOB_TYPES.DIRECT:
      path = getExternalPath({ pathName: CREATE_JOB_PATH, params: { instant: true } });
      break;
    case JOB_TYPES.SELECT:
      path = getExternalPath({ pathName: CREATE_JOB_PATH, params: { instant: false } });
      break;
    case JOB_TYPES.REHIRE:
      path = getExternalPath({ pathName: CREATE_REHIRE_JOB_PATH });
      break;

    default:
      break;
  }

  return path;
};

const renderPageDescription = (jobType) => {
  let description;
  switch (jobType) {
    case JOB_TYPES.DIRECT:
      description = <DirectDescription />;
      break;
    case JOB_TYPES.SELECT:
      description = <SelectDescription />;
      break;
    case JOB_TYPES.REHIRE:
      description = <RehireJobDescription />;
      break;

    default:
      break;
  }

  return description;
};

const JobTemplateOptions = ({ jobType }) => {
  const { data, fetchMore } = useQuery(GET_REPOSTABLE_JOBS, {
    variables: {
      first: DEFAULT_PAGE_SIZE,
    },
  });

  const repostableJobs = get(data, 'weployer.repostableJobs.nodes');
  const hasNextPage = get(data, 'weployer.repostableJobs.pageInfo.hasNextPage', true);

  const handleOnBottomReached = () => (
    fetchMore({
      query: GET_REPOSTABLE_JOBS,
      variables: {
        first: DEFAULT_PAGE_SIZE,
        after: get(data, 'weployer.repostableJobs.pageInfo.endCursor', ''),
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previousNodes = previousResult.weployer.repostableJobs.nodes;
        const newNodes = fetchMoreResult.weployer.repostableJobs.nodes;

        return {
          weployer: {
            repostableJobs: {
              pageInfo: {
                ...fetchMoreResult.weployer.repostableJobs.pageInfo,
                __typename: previousResult.weployer.repostableJobs.pageInfo.__typename,
              },
              nodes: [
                ...previousNodes,
                ...newNodes,
              ],
              __typename: previousResult.weployer.repostableJobs.__typename,
            },
            __typename: previousResult.weployer.__typename,
          },
        };
      },
    })
  );

  const handleChooseClick = (id) => {
    navigateExternal(repostJobPath(jobType, id));
  };

  const handleBlankJobPostButton = () => {
    navigateExternal(blankJobPath(jobType));
  };

  const renderJobsRepostingTable = (jobs) => {
    if (jobs && jobs.length) {
      return (
        <>
          <RepostableJobsTable
            jobs={jobs}
            onChooseClick={handleChooseClick}
          />
        </>
      );
    }

    return <JobsRepostingEmptyState />;
  };

  return (
    <StyledPageWrapper>
      <StyledPageHeader>
        <StyledPostBlankJobButton onClick={handleBlankJobPostButton}>
          <StyledPostBlankJobButtonContent>
            <Add />
            <span>Blank job post</span>
          </StyledPostBlankJobButtonContent>
        </StyledPostBlankJobButton>
        <StyledPageDescription>
          {renderPageDescription(jobType)}
        </StyledPageDescription>
      </StyledPageHeader>
      <StyledHeader>
        <StyledRepostIllustration src={repostIllustration} />
        <div>
          <StyledSectionTitle>Rapid Repost</StyledSectionTitle>
          <StyledSectionSubtitle>Use existing job details as a starting point</StyledSectionSubtitle>
        </div>
      </StyledHeader>

      <InfiniteScroll
        isEndReached={hasNextPage}
        onBottomReached={handleOnBottomReached}
      >
        { renderJobsRepostingTable(repostableJobs) }
      </InfiniteScroll>
    </StyledPageWrapper>
  );
};

JobTemplateOptions.propTypes = {
  jobType: PropTypes.string.isRequired,
};

export default JobTemplateOptions;
